import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../ui/badge';

const categoryLabels = {
  'ekonomi': 'Ekonomi',
  'politika': 'Politika',
  'teknoloji': 'Teknoloji',
  'finans': 'Finans',
  'dunya': 'Dünya',
  'vizyon-sektor': 'Vizyon Sektör',
};

const AUTOPLAY_MS = 5000;
const MAX_SLIDES = 12;

/**
 * Hero slider — horizontal sliding carousel with autoplay, pause-on-hover, dots.
 */
const HeroSlider = ({ items = [] }) => {
  const slides = items.slice(0, MAX_SLIDES);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((idx) => {
    if (slides.length === 0) return;
    setActive(((idx % slides.length) + slides.length) % slides.length);
  }, [slides.length]);

  const next = useCallback(() => {
    setActive((p) => (slides.length ? (p + 1) % slides.length : 0));
  }, [slides.length]);

  const prev = useCallback(() => {
    setActive((p) => (slides.length ? (p - 1 + slides.length) % slides.length : 0));
  }, [slides.length]);

  // Autoplay
  useEffect(() => {
    if (paused || slides.length <= 1) return;
    timerRef.current = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, next, slides.length]);

  if (slides.length === 0) return null;

  return (
    <div
      className="relative aspect-[16/10] rounded-lg overflow-hidden bg-slate-900 group/hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-slider"
    >
      {/* Slides track — horizontal sliding */}
      <div
        className="flex h-full w-full transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${active * 100}%)` }}
      >
        {slides.map((item, idx) => (
          <Link
            key={`hero-${item.id}`}
            to={`/haber/${item.slug || item.id}`}
            data-testid={`hero-slide-${idx}`}
            className="relative flex-shrink-0 w-full h-full block"
            aria-hidden={idx !== active}
            tabIndex={idx === active ? 0 : -1}
          >
            <img
              src={item.image_url}
              alt={item.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading={idx === 0 ? 'eager' : 'lazy'}
              draggable={false}
            />
            {/* Dark gradient — keeps white titles readable */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

            <Badge className="absolute top-4 left-4 bg-red-600 hover:bg-red-600 text-white">
              {categoryLabels[item.category] || item.category}
            </Badge>

            <div className="absolute bottom-10 left-4 right-4 md:bottom-12 md:left-6 md:right-6">
              <h1 className="text-lg sm:text-xl md:text-3xl font-bold text-white leading-tight drop-shadow-md line-clamp-3 md:line-clamp-none">
                {item.title}
              </h1>
            </div>
          </Link>
        ))}
      </div>

      {/* Navigation Arrows — visible on hover (always visible on touch devices) */}
      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); prev(); }}
            aria-label="Önceki haber"
            data-testid="hero-arrow-prev"
            className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 md:w-11 md:h-11 rounded-full bg-black/40 hover:bg-black/70 text-white shadow-lg backdrop-blur-sm ring-1 ring-white/10 transition-all duration-300 opacity-0 group-hover/hero:opacity-100 max-md:opacity-80 hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); next(); }}
            aria-label="Sonraki haber"
            data-testid="hero-arrow-next"
            className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 md:w-11 md:h-11 rounded-full bg-black/40 hover:bg-black/70 text-white shadow-lg backdrop-blur-sm ring-1 ring-white/10 transition-all duration-300 opacity-0 group-hover/hero:opacity-100 max-md:opacity-80 hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
          </button>
        </>
      )}

      {/* Pagination dots */}
      {slides.length > 1 && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm"
          data-testid="hero-dots"
        >
          {slides.map((_, idx) => {
            const isActive = idx === active;
            return (
              <button
                key={`dot-${idx}`}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  goTo(idx);
                }}
                aria-label={`Slayt ${idx + 1}`}
                aria-current={isActive ? 'true' : undefined}
                data-testid={`hero-dot-${idx}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  isActive
                    ? 'w-6 bg-white'
                    : 'w-1.5 bg-white/50 hover:bg-white/80'
                }`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HeroSlider;
